import type { CiCFeature, ProductInformation } from '@dealerportal/common';
import { useMemo } from 'react';

export type ItemContext = {
  itemId: string;
  instanceId: string;
  configurationState: IFeatureState[];
  instanceExtras: { selected: boolean };
};

/**
 * Strip the leading catalog-version id from a CiC id, leaving the manufacturer
 * code, e.g. `92784.cherry` -> `cherry`. Used as a best-effort source for
 * option ref codes, which the `CiCFeature` projection does not carry.
 */
export const stripCatalogVersion = (id: string): string =>
  id.split('.').slice(1).join('.');

/**
 * Build a SelectionViewer feature-state entry for a selected option.
 *
 * NOTE: `CiCFeature` is a lossy projection (see useCiCItemLookup) that drops
 * option ref codes, values, positioning characteristics and sub-item scopes,
 * so `optionRefCodes` is derived from the option id and the state is partial.
 */
export const toFeatureState = (
  feature: CiCFeature,
  optionId: string,
  source: IFeatureState['source']
): IFeatureState => ({
  featureId: feature.id,
  optionId,
  // CiCFeature keeps baseFeatureType as a plain string; the SelectionViewer
  // expects the cicero BaseFeatureType union, so bridge the projection here.
  featureClassification:
    feature.featureClassification as IFeatureState['featureClassification'],
  featureRefCodes: { manufCode: feature.code },
  optionRefCodes: { manufCode: stripCatalogVersion(optionId) },
  source,
});

/**
 * Build the SelectionViewer `configurationState` from the product's features,
 * defaulting each feature to its selected (or default) option.
 */
export const buildConfigurationState = (
  features: CiCFeature[]
): IFeatureState[] =>
  features.reduce<IFeatureState[]>((state, feature) => {
    const optionId = feature.selectedOptionIds[0] ?? feature.defaultOptionId;
    if (!optionId) return state;

    state.push(
      toFeatureState(
        feature,
        optionId,
        feature.selectedOptionIds.length > 0 ? 'user' : 'default'
      )
    );
    return state;
  }, []);

/**
 * Derive the Cyncly item context from a product. Each KBFr component builds its
 * own context independently; the `instanceId` is deterministic (derived from
 * `itemId`) so separately-mounted components agree and stay in sync over the
 * `item-context-list-update` postMessage bus.
 *
 * Returns `null` when the product has no features or slug to derive an id from.
 */
export const useKBFrItemContext = (
  product: ProductInformation
): ItemContext | null =>
  useMemo(() => {
    const features = product.features ?? [];
    // The catalog-version id prefixes every feature id (e.g. `92784.g_wood`).
    // `product.slug` is assumed to be the CiC item code (e.g. `b12l`).
    const catalogVersionId = features[0]?.id.split('.')[0];
    if (!catalogVersionId || !product.slug) return null;

    const itemId = `${catalogVersionId}.${product.slug}`;
    return {
      itemId,
      instanceId: `${itemId}_instance`,
      configurationState: buildConfigurationState(features),
      instanceExtras: { selected: true },
    };
  }, [product]);

/**
 * Broadcast an updated item context so a mounted SelectionViewer re-renders
 * with the new configuration state.
 */
export const emitContextListUpdate = (context: ItemContext): void => {
  window.postMessage(
    {
      type: 'item-context-list-update',
      itemContextList: [context],
    },
    '*'
  );
};
